import { create } from 'zustand';

/**
 * Local notifications store. Until the backend ships /v1/notifications,
 * the UI maintains an in-memory feed seeded with synthetic entries that
 * mirror what the operator would actually see in production: an import
 * just finished, a conflict needs arbitration, a key is expiring soon,
 * rejects are piling up in the DLQ.
 *
 * Mutations (push, dismiss, markAllRead) are exposed so other parts of
 * the app can wire real events as they become available (the import
 * wizard could `push` a notification on completion, for instance).
 */

export type NotificationKind = 'success' | 'warning' | 'info';

export type Notification = {
  id: string;
  kind: NotificationKind;
  icon: 'upload' | 'alert' | 'key' | 'inbox' | 'check' | 'shieldcheck';
  title: string;
  body: string;
  occurredAt: number; // epoch ms
  unread: boolean;
  href?: string; // optional navigation target
};

type State = {
  items: Notification[];
  unreadCount: number;
  push: (n: Omit<Notification, 'id' | 'occurredAt' | 'unread'>) => void;
  dismiss: (id: string) => void;
  markAllRead: () => void;
};

const SEED: Notification[] = [
  {
    id: 'demo-1',
    kind: 'success',
    icon: 'upload',
    title: 'Import Orange terminé',
    body: '18 391 lignes acceptées, 29 rejetées (à arbitrer dans la DLQ).',
    occurredAt: Date.now() - 6 * 60_000,
    unread: true,
    href: '/imports',
  },
  {
    id: 'demo-2',
    kind: 'warning',
    icon: 'alert',
    title: 'Conflit de réconciliation',
    body: 'Yao Brou — deux fiches PDE.CI divergentes pour le même numéro.',
    occurredAt: Date.now() - 14 * 60_000,
    unread: true,
    href: '/dossiers?reconStatus=CONFLIT',
  },
  {
    id: 'demo-3',
    kind: 'info',
    icon: 'key',
    title: 'Clé API bientôt expirée',
    body: 'PDE.CI Registre — expire dans 90 jours.',
    occurredAt: Date.now() - 2 * 60 * 60_000,
    unread: true,
    href: '/partners',
  },
  {
    id: 'demo-4',
    kind: 'info',
    icon: 'inbox',
    title: 'Rejets en attente',
    body: '7 messages en file DLQ à arbitrer.',
    occurredAt: Date.now() - 3 * 60 * 60_000,
    unread: false,
    href: '/admin',
  },
];

export const useNotifications = create<State>((set, get) => ({
  items: SEED,
  unreadCount: SEED.filter((n) => n.unread).length,
  push: (n) => {
    const item: Notification = {
      ...n,
      id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      occurredAt: Date.now(),
      unread: true,
    };
    const next = [item, ...get().items].slice(0, 50);
    set({ items: next, unreadCount: next.filter((x) => x.unread).length });
  },
  dismiss: (id) => {
    const next = get().items.filter((x) => x.id !== id);
    set({ items: next, unreadCount: next.filter((x) => x.unread).length });
  },
  markAllRead: () => {
    const next = get().items.map((x) => ({ ...x, unread: false }));
    set({ items: next, unreadCount: 0 });
  },
}));

export function relativeTime(epoch: number): string {
  const diff = Date.now() - epoch;
  if (diff < 60_000) return "à l'instant";
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  return `il y a ${d} j`;
}
